// Admin UI for choosing which properties appear in the Contact/Household filter
// popups, per team or as the tenant default (see
// docs/implementation-plans/2026-09-30-configurable-filter-properties.md).
import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { reloadIfCurrentTenant } from "../utils";
import { IoAdd, IoClose, IoSearch } from "react-icons/io5";
import client from "../api/client";
import { SectionCard } from "./ThemeColorFields";

const PAGE_TARGETS = [
  { value: "contact", label: "All contact pages (shared)" },
  { value: "database", label: "Database" },
  { value: "walking_list", label: "Walking List" },
  { value: "household", label: "All household pages (shared)" },
  { value: "households", label: "Households" },
  { value: "canvass_map", label: "Canvass Map" },
];
const SHARED_TARGETS = ["contact", "household"];
const DEFAULT_SOURCE_LABELS = {
  built_in: "the built-in set",
  tenant_default_shared: "the tenant default",
  tenant_default_page: "the tenant default for this page",
  team_shared: "this team's shared setting",
};
const scopeParams = (target) => (SHARED_TARGETS.includes(target) ? { object_type: target } : { page: target });
const DEFAULT_SCOPE = "default";

const FilterConfigManager = ({ tenantId, tenants = [], onTenantChange = () => {}, lockedTenant = null }) => {
  const [target, setTarget] = useState("contact");
  const [scope, setScope] = useState(DEFAULT_SCOPE);
  const [teams, setTeams] = useState([]);
  const [canEditDefault, setCanEditDefault] = useState(false);
  const [pool, setPool] = useState([]);
  const [maxConfigurable, setMaxConfigurable] = useState(0);
  const [selected, setSelected] = useState([]);
  const [configured, setConfigured] = useState(false);
  const [defaults, setDefaults] = useState([]);
  const [search, setSearch] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef(null);
  const [defaultSource, setDefaultSource] = useState("built_in");
  const [teamsLoaded, setTeamsLoaded] = useState(false);
  const [loadedFor, setLoadedFor] = useState(null);
  const [saving, setSaving] = useState(false);

  const teamId = scope === DEFAULT_SCOPE ? null : Number(scope);
  const currentKey = `${tenantId}|${target}|${scope}`;
  const loading = !teamsLoaded || loadedFor !== currentKey;

  useEffect(() => {
    let cancelled = false;
    setTeams([]);
    setTeamsLoaded(false);
    setScope(DEFAULT_SCOPE);
    if (!tenantId) return undefined;
    client
      .get(`/tenants/${tenantId}/filter-config/teams`)
      .then((res) => {
        if (cancelled) return;
        setTeams(res.data.teams || []);
        setCanEditDefault(Boolean(res.data.can_edit_default));
        if (!res.data.can_edit_default && res.data.teams?.length) setScope(String(res.data.teams[0].id));
        setTeamsLoaded(true);
      })
      .catch((error) => {
        if (!cancelled) toast.error(error.response?.data?.message || "Failed to load teams.");
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  useEffect(() => {
    let cancelled = false;
    setPool([]);
    if (!tenantId) return undefined;
    client
      .get(`/tenants/${tenantId}/filter-config/pool`, { params: scopeParams(target) })
      .then((res) => {
        if (cancelled) return;
        setPool(res.data.pool || []);
        setMaxConfigurable(res.data.max_configurable || 0);
      })
      .catch((error) => {
        if (!cancelled) toast.error(error.response?.data?.message || "Failed to load properties.");
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, target]);

  const canEditScope = scope === DEFAULT_SCOPE ? canEditDefault : true;

  useEffect(() => {
    let cancelled = false;
    if (!tenantId || !teamsLoaded || (scope === DEFAULT_SCOPE && !canEditDefault)) {
      setSelected([]);
      setConfigured(false);
      return undefined;
    }
    const params = scopeParams(target);
    if (teamId) params.team_id = teamId;
    client
      .get(`/tenants/${tenantId}/filter-config`, { params })
      .then((res) => {
        if (cancelled) return;
        const defaultKeys = res.data.default_properties || [];
        setDefaults(defaultKeys);
        setDefaultSource(res.data.default_source || "built_in");
        setSelected(res.data.configured ? res.data.properties || [] : defaultKeys);
        setConfigured(Boolean(res.data.configured));
        setLoadedFor(currentKey);
      })
      .catch((error) => {
        if (!cancelled) toast.error(error.response?.data?.message || "Failed to load filter settings.");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, target, scope, canEditDefault, teamsLoaded]);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) setPickerOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, []);

  const addProperty = (key) => {
    setSelected((prev) => (prev.includes(key) || prev.length >= maxConfigurable ? prev : [...prev, key]));
  };

  const removeProperty = (key) => {
    setSelected((prev) => prev.filter((k) => k !== key));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await client.put(`/tenants/${tenantId}/filter-config`, {
        ...scopeParams(target),
        team_id: teamId,
        properties: selected,
      });
      setConfigured(true);
      toast.success("Filter properties saved.");
      reloadIfCurrentTenant(tenantId);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save filter properties.");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      const params = scopeParams(target);
      if (teamId) params.team_id = teamId;
      await client.delete(`/tenants/${tenantId}/filter-config`, { params });
      setSelected(defaults);
      setConfigured(false);
      toast.success("Filter properties reset.");
      reloadIfCurrentTenant(tenantId);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to reset filter properties.");
    } finally {
      setSaving(false);
    }
  };

  const atLimit = selected.length >= maxConfigurable;
  const labelFor = (key) => pool.find((property) => property.key === key)?.label || key;
  const availableProperties = pool.filter(
    (property) =>
      !selected.includes(property.key) &&
      property.label.toLowerCase().includes(search.trim().toLowerCase())
  );

  return (
    <SectionCard
      title="Filter Properties"
      description="Choose which properties appear in the Contact and Household filter popups. A team's selection overrides the tenant default. List Membership is always shown."
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap gap-4">
          {!lockedTenant && (
            <div className="flex flex-col gap-2 w-[260px]">
              <h6 className="text-canvas-directoryText">Tenant</h6>
              <select
                aria-label="Tenant"
                className="border border-canvas-tersery rounded px-3 py-2 text-sm font-outfit h-[42px]"
                value={tenantId || ""}
                onChange={(e) => onTenantChange(e.target.value ? Number(e.target.value) : null)}
              >
                {tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>{tenant.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="flex flex-col gap-2 w-[260px]">
            <h6 className="text-canvas-directoryText">Page</h6>
            <select
              aria-label="Page"
              className="border border-canvas-tersery rounded px-3 py-2 text-sm font-outfit h-[42px]"
              value={target}
              onChange={(e) => {
                setTarget(e.target.value);
                setSearch("");
              }}
            >
              {PAGE_TARGETS.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-2 w-[260px]">
            <h6 className="text-canvas-directoryText">Applies to</h6>
            <select
              aria-label="Applies to"
              className="border border-canvas-tersery rounded px-3 py-2 text-sm font-outfit h-[42px]"
              value={scope}
              disabled={!tenantId || (teamsLoaded && !canEditDefault && teams.length === 0)}
              onChange={(e) => {
                setScope(e.target.value);
                setSearch("");
              }}
            >
              {canEditDefault && <option value={DEFAULT_SCOPE}>Tenant default</option>}
              {teams.map((team) => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>
          </div>
        </div>

        {!tenantId ? null : !teamsLoaded ? (
          <h6 className="text-canvas-directoryText">Loading...</h6>
        ) : !canEditScope ? (
          <h6 className="text-canvas-directoryText">
            {teams.length === 0
              ? "You are not a member of any team in this tenant, so there are no teams for you to configure. Teams are synced from HubSpot; ask an admin to sync teams or add you to one."
              : "You can only configure filters for your own teams."}
          </h6>
        ) : (
          <div className="flex flex-col gap-4">
            <p className="text-sm font-outfit text-canvas-secondery">
              {configured
                ? "Saved for this selection."
                : `Nothing saved yet, showing ${DEFAULT_SOURCE_LABELS[defaultSource] || DEFAULT_SOURCE_LABELS.built_in}. Saving makes this selection explicit.`}
            </p>

            {loading ? (
              <h6 className="text-canvas-directoryText">Loading...</h6>
            ) : (
              <div className="flex flex-col gap-5">
                <div ref={pickerRef} className="relative w-full lg:w-[520px]">
                  <label className="block mb-2 text-sm font-semibold font-outfit text-canvas-directoryText" htmlFor="filter-property-search">
                    Add a property
                  </label>
                  <div className="relative">
                    <IoSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-canvas-secondery" />
                    <input
                      id="filter-property-search"
                      type="search"
                      aria-label="Search properties"
                      aria-expanded={pickerOpen}
                      autoComplete="off"
                      placeholder="Search properties to add..."
                      value={search}
                      onFocus={() => setPickerOpen(true)}
                      onClick={() => setPickerOpen(true)}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPickerOpen(true);
                      }}
                      onKeyDown={(e) => e.key === "Escape" && setPickerOpen(false)}
                      className="border border-canvas-tersery rounded-lg pl-10 pr-3 text-sm font-outfit h-[44px] w-full focus:outline-none focus:border-canvas-primary"
                    />
                  </div>

                  {pickerOpen && (
                    <div className="absolute z-20 left-0 right-0 mt-1 bg-canvas-white border border-canvas-tersery rounded-lg shadow-lg overflow-hidden">
                      <ul className="max-h-[280px] overflow-y-auto py-1" aria-label="Available properties">
                        {availableProperties.length === 0 && (
                          <li className="px-4 py-3 text-sm font-outfit text-canvas-secondery">
                            {search ? "No matching properties." : "All properties are selected."}
                          </li>
                        )}
                        {availableProperties.map((property) => (
                          <li key={property.key}>
                            <button
                              type="button"
                              aria-label={`Add ${property.label}`}
                              disabled={atLimit}
                              onClick={() => addProperty(property.key)}
                              className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-sm font-outfit text-left hover:bg-canvas-userbar disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                            >
                              <span>{property.label}</span>
                              <span className="inline-flex items-center gap-1 text-canvas-primary font-semibold">
                                <IoAdd aria-hidden="true" /> Add
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                      {atLimit && (
                        <p className="px-4 py-2 text-xs font-outfit text-canvas-secondery border-t border-canvas-tersery">
                          Maximum of {maxConfigurable} reached. Remove a property to add another.
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h6 className="font-semibold text-canvas-directoryText">Selected properties</h6>
                    <span className="text-xs font-outfit font-semibold text-canvas-primary bg-canvas-userbar rounded-full px-3 py-1">
                      Selected {selected.length} of {maxConfigurable}
                    </span>
                  </div>
                  <ol className="flex flex-wrap gap-2" aria-label="Selected properties">
                    {selected.length === 0 && (
                      <li className="w-full text-sm font-outfit text-canvas-secondery border border-dashed border-canvas-tersery rounded-lg px-4 py-3 text-center">
                        No properties selected.
                      </li>
                    )}
                    {selected.map((key, index) => {
                      const label = labelFor(key);
                      return (
                        <li
                          key={key}
                          className="inline-flex items-center gap-2 border border-canvas-tersery rounded-full pl-1.5 pr-1.5 py-1 bg-canvas-userbar"
                        >
                          <div className="w-5 h-5 rounded-full bg-canvas-primary text-white text-[11px] font-outfit flex items-center justify-center shrink-0">
                            {index + 1}
                          </div>
                          <span data-testid="selected-label" className="text-sm font-outfit">{label}</span>
                          <button
                            type="button"
                            aria-label={`Remove ${label}`}
                            onClick={() => removeProperty(key)}
                            className="w-5 h-5 rounded-full flex items-center justify-center text-canvas-secondery hover:bg-canvas-danger hover:text-white shrink-0"
                          >
                            <IoClose aria-hidden="true" />
                          </button>
                        </li>
                      );
                    })}
                  </ol>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || loading}
                className="bg-canvas-primary text-white rounded px-5 py-2 text-sm font-outfit disabled:opacity-60"
              >
                {saving ? "Saving..." : "Save"}
              </button>
              {configured && (
                <button
                  type="button"
                  onClick={handleReset}
                  disabled={saving || loading}
                  className="border border-canvas-tersery rounded px-5 py-2 text-sm font-outfit disabled:opacity-60"
                >
                  Reset to default
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </SectionCard>
  );
};

export default FilterConfigManager;
